import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Printer, Sparkles, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { GoogleReviewCta } from '../components/GoogleReviewCta';

const Home = () => {
  const { t } = useLanguage();

  const serviceCards = [
    {
      key: 'metalwork',
      icon: Wrench,
      image: 'https://images.unsplash.com/photo-1714504904786-b6732390b206',
    },
    {
      key: 'print3d',
      icon: Printer,
      image: 'https://images.unsplash.com/photo-1752614672115-adcf3e2596af',
    },
    {
      key: 'engraving',
      icon: Sparkles,
      image: 'https://images.unsplash.com/photo-1739850269293-8ed949c32b22',
    },
    {
      key: 'scaffolding',
      icon: Building2,
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white dark:from-black dark:via-gray-900 dark:to-black transition-colors duration-200">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1714504904786-b6732390b206"
            alt="Fabrikov"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white dark:from-black/70 dark:via-black/50 dark:to-black"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
            {t('home.heroTitle1')}
            <span className="block text-green-600 dark:text-green-400 mt-2">{t('home.heroTitle2')}</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            {t('home.heroSubtitle')}
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            {t('home.heroDescription')}
          </p>
          <Link
            to="/kontakt"
            data-testid="hero-cta-btn"
            className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black font-semibold px-8 py-4 rounded-lg transform hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/20"
          >
            <span>{t('home.ctaQuote')}</span>
            <ArrowRight size={20} />
          </Link>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-green-600 dark:bg-green-400 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('home.servicesTitle')}</h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg">{t('home.servicesSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceCards.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.key}
                  data-testid={`service-card-${service.key}`}
                  className="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:border-green-500 transition-all duration-300 transform hover:-translate-y-2 shadow-lg"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={t(`services.items.${service.key}.title`)}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center shadow-lg">
                      <Icon size={24} className="text-white dark:text-black" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {t(`services.items.${service.key}.title`)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                      {t(`services.items.${service.key}.description`)}
                    </p>
                    <Link
                      to="/sluzby"
                      className="inline-flex items-center space-x-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium text-sm transition-colors duration-200"
                    >
                      <span>{t('home.moreInfo')}</span>
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <GoogleReviewCta />

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-12 text-center shadow-xl">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
            {t('home.ctaText')}
          </p>
          <Link
            to="/kontakt"
            data-testid="home-cta-bottom-btn"
            className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white dark:text-black font-semibold px-8 py-4 rounded-lg transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <span>{t('home.ctaButton')}</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
